import React, { useState, useEffect } from "react";
import HacButton from "components/HacButton";
import HacText from "components/HacText";
import HacFlex from "components/HacFlex";
import useWordByWordTranscription from "hooks/useWordByWordTranscription";
import { CircleCheck, X, ArrowLeft, ArrowRight } from "tabler-icons-react";
import WordRecordingButton from "pages/home/components/WordRecordingButton";
import { TTS_API_URL } from "routes/api";
import axios from "axios";
import useQueryParams from "hooks/useQueryParams";
import { useSendWordByWordTranscriptionMutation } from "redux_dir/features/diagnostiqueApi";
import { toast } from "sonner";

const WordByWordTranscription = (props) => {
    const { data, onComplete, context = "diagnostic" } = props; // Ajouter context prop

    const [currentWordIndex, setCurrentWordIndex] = useState(0);
    const [transcriptions, setTranscriptions] = useState([]);
    const [audioFiles, setAudioFiles] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [apiResults, setApiResults] = useState(null);
    const [isCompleted, setIsCompleted] = useState(false);

    // QueryParams hooks (comme les autres diagnostics)
    const { queryParams, getCurrentParams, setQueryParams } = useQueryParams();
    const currentParams = getCurrentParams();

    // Utiliser le hook personnalisé
    const { sendTranscriptionData, validateTranscriptionData, isSubmitting, error } = useWordByWordTranscription();

    // Utiliser la mutation RTK Query (le token CSRF sera ajouté automatiquement)
    const [sendWordByWordTranscription, { isLoading: isSendingTranscription }] = useSendWordByWordTranscriptionMutation();

    const consigne = data?.consigne || [];
    const correction = data?.correction_dp || [];

    useEffect(() => {
        // Nouvel exercice : tout réinitialiser, y compris l'index du mot courant.
        // Sans ce reset, `currentWordIndex` gardait la position de l'exercice
        // précédent (ex: 11/11 après un exercice de 12 mots) alors que le nouvel
        // exercice peut avoir moins de mots : on écrivait alors dans un index
        // hors de portée du tableau `transcriptions`, `areAllWordsTranscribed`
        // ne devenait jamais vrai, et le micro restait bloqué en boucle.
        setCurrentWordIndex(0);
        setTranscriptions(consigne.map(() => ""));
        setAudioFiles(consigne.map(() => null));
        setApiResults(null);
        setIsCompleted(false);
        setIsProcessing(false);

        // Initialiser les query params comme les autres diagnostics
        setQueryParams({
            ...currentParams,
            note: 0,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [consigne]);

    // Fonction pour gérer l'enregistrement d'un mot
    const handleWordRecording = (transcription, audioFile) => {
        if (audioFile) {
            // Stocker l'audio pour ce mot
            const newAudioFiles = [...audioFiles];
            newAudioFiles[currentWordIndex] = audioFile;
            setAudioFiles(newAudioFiles);

            // Marquer ce mot comme transcrit
            const newTranscriptions = [...transcriptions];
            newTranscriptions[currentWordIndex] = transcription || "enregistré";
            setTranscriptions(newTranscriptions);

            console.log(`Mot ${currentWordIndex + 1} "${consigne[currentWordIndex]}" enregistré:`, {
                audioFile: audioFile.name,
                size: audioFile.size,
                type: audioFile.type
            });

            // Passer au mot suivant automatiquement
            if (currentWordIndex < consigne.length - 1) {
                setCurrentWordIndex(currentWordIndex + 1);
            }
        }
    };

    // Fonction pour récupérer les transcriptions avec score_binaire (comme dans la transcription simple)
    const getTranscriptionsWithScore = async () => {
        try {
            console.log("Récupération des transcriptions depuis:", `${TTS_API_URL}transcriptions/getAll/`);
            const res = await axios.get(`${TTS_API_URL}transcriptions/getAll/`);
            console.log("Transcriptions récupérées:", res?.data);
            return res?.data?.results || [];
        } catch (error) {
            console.error("Erreur lors de la récupération des transcriptions:", error);
            return [];
        }
    };

    // Fonction pour faire l'envoi global avec tous les fichiers audios et targets
    const handleFinalSubmission = async (audioFilesToSend = null) => {
        console.log("=== DÉBUT handleFinalSubmission ===");
        setIsProcessing(true);

        // Utiliser les audios passés en paramètre ou ceux du state
        const audiosToProcess = audioFilesToSend || audioFiles;

        try {
            // Vérifier que tous les audios sont présents
            const missingAudios = audiosToProcess.filter(audio => !audio);
            if (missingAudios.length > 0) {
                console.error("Il manque des enregistrements audio !");
                console.log("Audios disponibles:", audiosToProcess);
                alert("Veuillez enregistrer tous les mots avant de continuer.");
                setIsProcessing(false);
                return;
            }

            // Créer FormData avec tous les fichiers audios et targets
            const formData = new FormData();

            console.log("=== DEBUG: Données à envoyer ===");
            console.log("Consigne complète:", consigne);
            console.log("AudioFiles:", audiosToProcess);
            console.log("Nombre de mots dans consigne:", consigne.length);
            console.log("Nombre d'audios enregistrés:", audiosToProcess.filter(audio => audio !== null).length);

            // Ajouter tous les targets et audios avec des noms uniques
            for (let i = 0; i < consigne.length; i++) {
                if (audiosToProcess[i]) {
                    formData.append(`items[${i}][target]`, consigne[i]);
                    // Donner un nom unique à chaque fichier audio
                    const fileName = `audio_${i + 1}_${consigne[i]}.mp3`;
                    formData.append(`items[${i}][audio]`, audiosToProcess[i], fileName);
                    console.log(`Mot ${i + 1}: "${consigne[i]}" - Audio: ${fileName} (${audiosToProcess[i].size} bytes)`);
                } else {
                    console.log(`Mot ${i + 1}: "${consigne[i]}" - Audio: MANQUANT`);
                }
            }

            console.log("=== FIN DEBUG ===");

            // Utiliser la mutation RTK Query (le token sera ajouté automatiquement par apiMananger)
            const response = await sendWordByWordTranscription({
                formData,
            }).unwrap();

            const result = response;
            console.log("=== RÉPONSE API ===");
            console.log("Résultat complet:", result);
            console.log("Nombre total de mots:", result.nombre_mot_total);
            console.log("Mots mal lus:", result.mal_lus);
            console.log("Mots bien lus:", result.nombre_mot_bienlus);
            console.log("Performance:", result.performance);
            console.log("=== FIN RÉPONSE API ===");

            // Traiter les résultats de l'API
            const { mal_lus, nombre_mot_bienlus, nombre_mot_malus, nombre_mot_total, performance } = result;

            // Créer les transcriptions basées sur les résultats
            const newTranscriptions = consigne.map((mot, index) => {
                const isMalLu = mal_lus.some(item => item.mot === mot);
                return isMalLu ? "mal_lu" : mot;
            });

            // Mettre à jour les transcriptions
            setTranscriptions(newTranscriptions);

            // Sauvegarder les résultats de l'API
            setApiResults(result);

            // Calculer le score final (comme la transcription simple)
            // Si performance >= 0.8, donner le bareme complet, sinon 0
            const score = result.performance >= 0.8 ? data.bareme : 0;

            // Mettre à jour les query params
            setQueryParams({
                ...currentParams,
                note: score,
                completed: true,
                results: result
            });

            // Signaler la completion au parent (comme les autres diagnostics)
            if (onComplete) {
                console.log("=== Appel de onComplete ===");
                console.log("Score:", score);
                console.log("Bareme:", data.bareme);
                console.log("Performance:", result.performance);
                console.log("onComplete function:", typeof onComplete);

                onComplete({
                    score: score,
                    bareme: data.bareme,
                    performance: result.performance,
                    details: result
                });
            }

            // Marquer l'exercice comme terminé
            setIsCompleted(true);

        } catch (error) {
            console.error("Error during global submission:", error);
            
            // Vérifier si c'est une erreur d'indisponibilité de l'IA
            if (error?.status === 503 || error?.data?.error === "AI_UNAVAILABLE") {
                toast.error("Assistant IA momentanément indisponible", {
                    duration: 5000,
                    style: {
                        background: '#ef4444',
                        color: '#fff',
                    },
                });
            } else {
                toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
            }
        } finally {
            setIsProcessing(false);
        }
    };

    // Fonction pour revenir au mot précédent
    const handlePreviousWord = () => {
        if (currentWordIndex > 0) {
            setCurrentWordIndex(currentWordIndex - 1);
        }
    };

    // Fonction pour réinitialiser la transcription du mot actuel
    const resetCurrentWord = () => {
        const newTranscriptions = [...transcriptions];
        newTranscriptions[currentWordIndex] = "";
        setTranscriptions(newTranscriptions);

        const newAudioFiles = [...audioFiles];
        newAudioFiles[currentWordIndex] = null;
        setAudioFiles(newAudioFiles);
    };

    // Vérifier si le mot actuel est transcrit
    const isCurrentWordTranscribed = transcriptions[currentWordIndex] && transcriptions[currentWordIndex].trim() !== "";

    // Vérifier si tous les mots sont transcrits
    const areAllWordsTranscribed = transcriptions.every(t => t && t.trim() !== "");

    return (
        <div className="h-full flex flex-col justify-between">
            {/* Bouton d'enregistrement */}
            <div className="p-4">
                <WordRecordingButton
                    onTranscription={handleWordRecording}
                    targetWord={consigne[currentWordIndex]}
                    onReset={resetCurrentWord}
                    disabled={isCurrentWordTranscribed}
                    currentIndex={currentWordIndex}
                    totalCount={consigne.length}
                    valid={areAllWordsTranscribed}
                    onClickButton={() => handleFinalSubmission()}
                    isRequesting={isProcessing}
                />
            </div>
        </div>
    );
};

export default WordByWordTranscription; 